import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { changePassword, login as cognitoLogin } from "@/lib/cognito";

export function AccountTab() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const passwordValid =
    newPassword.length >= 12 &&
    /[A-Z]/.test(newPassword) &&
    /[a-z]/.test(newPassword) &&
    /[0-9]/.test(newPassword) &&
    /[^A-Za-z0-9]/.test(newPassword);

  const canSubmit =
    currentPassword.length > 0 &&
    passwordValid &&
    newPassword === confirmPassword &&
    status !== "saving";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("saving");
    setErrorMessage("");

    try {
      // Cognito ChangePassword requires the raw access token (not ID token).
      // Re-authenticate with current password to get a fresh access token.
      const loginResult = await cognitoLogin(user!.email, currentPassword);
      if (loginResult.type !== "authenticated") {
        throw new Error("Unexpected challenge during re-authentication");
      }

      await changePassword(loginResult.accessToken, currentPassword, newPassword);
      setStatus("success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setStatus("error");
      const message = err instanceof Error ? err.message : "Failed to change password";
      setErrorMessage(message);
    }
  };

  return (
    <div className="max-w-md space-y-6">
      <div>
        <h3 className="text-lg font-medium">Account</h3>
        <p className="text-sm text-muted-foreground">
          Signed in as {user?.email}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="current-password">Current Password</Label>
          <Input
            id="current-password"
            type="password"
            value={currentPassword}
            onChange={(e) => { setCurrentPassword(e.target.value); setStatus("idle"); }}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="new-password">New Password</Label>
          <Input
            id="new-password"
            type="password"
            value={newPassword}
            onChange={(e) => { setNewPassword(e.target.value); setStatus("idle"); }}
          />
          <p className="text-xs text-muted-foreground">
            12+ characters, uppercase, lowercase, number, symbol
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm New Password</Label>
          <Input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setStatus("idle"); }}
          />
          {confirmPassword.length > 0 && newPassword !== confirmPassword && (
            <p className="text-xs text-red-400">Passwords do not match</p>
          )}
        </div>

        {status === "error" && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
            {errorMessage}
          </div>
        )}

        {status === "success" && (
          <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-400">
            Password changed successfully.
          </div>
        )}

        <Button type="submit" disabled={!canSubmit}>
          {status === "saving" ? "Changing..." : "Change Password"}
        </Button>
      </form>
    </div>
  );
}
